import {Clock, Scene, Vector2, WebGLRenderer} from 'three'

import type {
  MonaCanvasBounds,
  MonaCanvasFaceDirection,
  MonaCanvasFraming,
  MonaCanvasMouseInteractionType,
  MonaCanvasOpeningAnimation,
} from '../types'
import type {MonaColorVariant} from '../config'
import FirstScene from './first-scene'

const CANVAS_SIZE_MULTIPLIER = 1.25

export default class Artwork {
  renderer: WebGLRenderer
  scene: Scene = new Scene()
  pixelRatio: number = 1
  canvas: HTMLCanvasElement
  firstScene: FirstScene
  wrapper: HTMLElement
  monaViewportCenter = new Vector2()
  monaViewportSize = new Vector2()
  isActive = false
  clock = new Clock(false)
  wrapperWidth = 0
  wrapperHeight = 0
  canvasWidth = 0
  canvasHeight = 0
  canvasLeft: number | undefined
  canvasTop: number | undefined

  constructor(props: {
    canvas: HTMLCanvasElement
    colorVariant: MonaColorVariant
    createOpeningAnimation?: () => MonaCanvasOpeningAnimation
    framing?: MonaCanvasFraming
    onError: (error: unknown) => void
    onOpeningStart?: () => void
    onOpeningComplete?: () => void
    pixelRatio: number
    reduceMotion: boolean
    wrapper: HTMLElement
  }) {
    this.canvas = props.canvas
    this.wrapper = props.wrapper
    this.firstScene = new FirstScene(
      props.colorVariant,
      props.onError,
      props.reduceMotion,
      props.createOpeningAnimation,
      props.onOpeningStart,
      props.onOpeningComplete,
      props.framing,
    )

    const renderer = new WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
    })
    this.renderer = renderer
    renderer.setClearColor(0xffffff, 0.0)
    this.pixelRatio = props.pixelRatio
    renderer.setPixelRatio(this.pixelRatio)

    this.scene.add(this.firstScene.group)
    this.firstScene.hide()
  }

  resize(width: number, height: number) {
    if (width <= 0 || height <= 0) return

    this.wrapperWidth = width
    this.wrapperHeight = height
    this.firstScene.resize(width, height)
    this.updateCanvasViewport()
  }

  update(isInViewport: boolean, isVisible: boolean): MonaCanvasBounds | null {
    const isActive = isVisible && (isInViewport || this.isActive)

    if (!isActive) {
      if (this.isActive) {
        this.clock.stop()
        this.firstScene.hide()
        this.isActive = false
      }
      return null
    }

    const delta = this.isActive ? this.clock.getDelta() : 0
    if (!this.isActive) {
      this.clock.start()
      this.firstScene.show()
      this.isActive = true
    }

    this.firstScene.update(delta)
    // Keep movement current outside the viewport without drawing or updating canvas styles.
    if (!isInViewport) return null

    const monaCanvasBounds = this.updateCanvasViewport()
    this.renderer.setRenderTarget(null)
    this.renderer.render(this.scene, this.firstScene.camera)
    return monaCanvasBounds
  }

  dispose() {
    this.clock.stop()
    this.firstScene.dispose()
    this.renderer.dispose()
  }

  restartOpening() {
    this.firstScene.restartOpening()
    if (this.isActive) this.clock.start()
  }

  skipOpening() {
    this.firstScene.skipOpening()
  }

  setModelScale(scale: number) {
    this.firstScene.setModelScale(scale)
  }

  handleMouseMove(event: MouseEvent) {
    this.firstScene.handleMouseMove(event, this.wrapper.getBoundingClientRect())
  }

  setMouseInteraction(type: MonaCanvasMouseInteractionType, radius?: number, restingPitch?: number) {
    this.firstScene.setMouseInteraction(type, radius, restingPitch)
  }

  resetMouseFacing() {
    this.firstScene.resetMouseFacing()
  }

  setFaceDirection(direction?: MonaCanvasFaceDirection | null) {
    this.firstScene.setFaceDirection(direction)
  }

  updateCanvasViewport(): MonaCanvasBounds | null {
    if (this.wrapperWidth <= 0 || this.wrapperHeight <= 0) return null

    const hasMonaBounds = this.firstScene.getMonaViewportMetrics(
      this.wrapperHeight,
      this.monaViewportCenter,
      this.monaViewportSize,
    )
    const canvasSize = hasMonaBounds
      ? Math.max(1, Math.ceil(Math.max(this.monaViewportSize.x, this.monaViewportSize.y) * CANVAS_SIZE_MULTIPLIER))
      : null
    const width = canvasSize ?? this.wrapperWidth
    const height = canvasSize ?? this.wrapperHeight
    const centerX = hasMonaBounds ? this.monaViewportCenter.x * this.wrapperWidth : this.wrapperWidth * 0.5
    const centerY = hasMonaBounds ? this.monaViewportCenter.y * this.wrapperHeight : this.wrapperHeight * 0.5
    const left = centerX - width * 0.5
    const top = centerY - height * 0.5

    if (width !== this.canvasWidth || height !== this.canvasHeight) {
      this.canvasWidth = width
      this.canvasHeight = height
      this.renderer.setSize(width, height, false)
      this.canvas.style.width = `${width}px`
      this.canvas.style.height = `${height}px`
      this.firstScene.setResolution(this.canvas.width, this.canvas.height)
    }

    if (left !== this.canvasLeft || top !== this.canvasTop) {
      this.canvasLeft = left
      this.canvasTop = top
      this.canvas.style.transform = `translate3d(${left}px, ${top}px, 0)`
    }
    this.firstScene.setRenderViewport(this.wrapperWidth, this.wrapperHeight, left, top, width, height)

    return hasMonaBounds
      ? {
          centerX: this.monaViewportCenter.x,
          centerY: this.monaViewportCenter.y,
          height: height / this.wrapperHeight,
          width: width / this.wrapperWidth,
        }
      : null
  }
}
